import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AdminChannel, type CurrentUser, type UserRole } from '../lib/api';
import { Notifications } from './Notifications';
import { SheetSync } from './SheetSync';
import { Logs } from './Logs';
import { AdminSales } from './AdminSales';

/**
 * Admin: channel readiness and user accounts.
 *
 * Credentials are write-only over the API, so this screen shows whether a
 * channel works, never what its secrets are.
 */

export function Admin({ user }: { user: CurrentUser }) {
  const [tab, setTab] = useState<'channels' | 'sales' | 'users' | 'alerts' | 'sheet' | 'stuck' | 'logs'>('channels');

  return (
    <div>
      <div className="toolbar">
        <button className={tab === 'channels' ? 'active' : ''} onClick={() => setTab('channels')}>
          Channels
        </button>
        <button className={tab === 'sales' ? 'active' : ''} onClick={() => setTab('sales')}>
          Sales data
        </button>
        <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>
          Users
        </button>
        <button className={tab === 'alerts' ? 'active' : ''} onClick={() => setTab('alerts')}>
          Alerts
        </button>
        <button className={tab === 'sheet' ? 'active' : ''} onClick={() => setTab('sheet')}>
          Google Sheet
        </button>
        <button className={tab === 'stuck' ? 'active' : ''} onClick={() => setTab('stuck')}>
          Stuck writes
        </button>
        <button className={tab === 'logs' ? 'active' : ''} onClick={() => setTab('logs')}>
          Logs
        </button>
      </div>
      {tab === 'channels' && <Channels />}
      {tab === 'sales' && <AdminSales />}
      {tab === 'users' && <Users currentUser={user} />}
      {tab === 'alerts' && <Notifications />}
      {tab === 'sheet' && <SheetSync />}
      {tab === 'stuck' && <StuckWrites />}
      {tab === 'logs' && <Logs />}
    </div>
  );
}

/**
 * What each channel type needs entered by hand.
 *
 * Credentials are write-only over the API, so these fields always start empty —
 * there is nothing to prefill them with. The server merges what it is sent, so
 * a field left blank keeps whatever is already stored.
 */
const credentialFields: Record<
  string,
  Array<{ name: string; label: string; hint?: string; secret?: boolean; required?: boolean }>
> = {
  drupal_uc: [
    {
      name: 'baseUrl',
      label: 'Bridge URL',
      hint: 'e.g. https://www.midwestlogfurniture.com/mfs-bridge',
      required: true,
    },
    {
      name: 'bridgeKey',
      label: 'Bridge key',
      hint: 'mfs_bridge_key from the site settings.php',
      secret: true,
      required: true,
    },
  ],
  drupal_commerce: [
    {
      name: 'baseUrl',
      label: 'Bridge URL',
      hint: 'e.g. https://www.midwestfarmhouse.com/mfs-bridge',
      required: true,
    },
    {
      name: 'bridgeKey',
      label: 'Bridge key',
      hint: 'mfs_bridge_key from the site settings.php',
      secret: true,
      required: true,
    },
  ],
  amazon: [
    {
      name: 'sellerId',
      label: 'Merchant token',
      hint: 'Seller Central, under Account Info',
      required: true,
    },
    {
      name: 'refreshToken',
      label: 'LWA refresh token',
      hint: 'From self-authorization; begins Atzr|',
      secret: true,
      required: true,
    },
  ],
  etsy: [
    { name: 'shopId', label: 'Shop id', hint: 'Numeric, not the shop name', required: true },
    { name: 'keystring', label: 'App keystring', hint: 'Each shop has its own Etsy app' },
    { name: 'sharedSecret', label: 'App shared secret', secret: true },
  ],
};

function Channels() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({ queryKey: ['admin-channels'], queryFn: api.adminChannels });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin-channels'] });

  if (isLoading) return <div className="empty-state">Loading…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            <th>Channel</th>
            <th>Type</th>
            <th>Status</th>
            <th>Listings</th>
            <th>Last sync</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((channel) => (
            <Fragment key={channel.id}>
              <tr>
                <td>
                  {channel.name}
                  {!channel.enabled && <> <span className="badge">disabled</span></>}
                </td>
                <td className="muted small">{channel.type}</td>
                <td>
                  {channel.readiness.ready ? (
                    <span className="badge ok">ready</span>
                  ) : (
                    <span className="badge error" title={channel.readiness.reason}>
                      not configured
                    </span>
                  )}
                  {channel.readiness.warning && (
                    <>
                      {' '}
                      {/* Whether the date needs a person is the difference
                          between acting and ignoring, so the badge says which
                          rather than making both look identical. */}
                      <span className="badge warn" title={channel.readiness.warning}>
                        {channel.readiness.renewal === 'automatic'
                          ? 'renews on sync'
                          : 'reconnect needed'}
                      </span>
                    </>
                  )}
                  {(channel.readiness.warning || !channel.readiness.ready) && (
                    <div className="small muted">
                      {channel.readiness.reason ?? channel.readiness.warning}
                    </div>
                  )}
                </td>
                <td>{channel.listingCount}</td>
                <td className="small muted">
                  {channel.lastSync
                    ? `${new Date(channel.lastSync.startedAt).toLocaleString()} ${channel.lastSync.ok ? '' : '(failed)'}`
                    : 'never'}
                </td>
                <td>
                  {/* eBay has nothing to type: its app keyset is environment
                      configuration, so consent is the only step and there is no
                      credential form to offer. */}
                  {channel.type === 'ebay' ? (
                    <OAuthConnect
                      channel={channel}
                      provider="eBay"
                      authorize={api.ebayAuthorize}
                      onDone={refresh}
                    />
                  ) : credentialFields[channel.type] ? (
                    <>
                      {channel.type === 'etsy' && channel.hasCredentials && (
                        <>
                          <OAuthConnect
                            channel={channel}
                            provider="Etsy"
                            authorize={api.etsyAuthorize}
                            onDone={refresh}
                          />{' '}
                        </>
                      )}
                      <button
                        className="link small"
                        onClick={() => setEditing((v) => (v === channel.id ? null : channel.id))}
                      >
                        {editing === channel.id
                          ? 'close'
                          : channel.hasCredentials
                            ? 'replace credentials'
                            : 'add credentials'}
                      </button>
                    </>
                  ) : (
                    <span className="muted small">not configurable here</span>
                  )}
                </td>
              </tr>
              {editing === channel.id && (
                <tr>
                  <td colSpan={6}>
                    <CredentialForm
                      channel={channel}
                      onSaved={() => {
                        setEditing(null);
                        refresh();
                      }}
                    />
                  </td>
                </tr>
              )}
              {/* Only Amazon carries a business (B2B) price alongside its
                  retail one — see CHANNEL_CAPABILITIES.canWriteBusinessPrice. */}
              {channel.type === 'amazon' && (
                <tr>
                  <td colSpan={6}>
                    <BusinessPricingRow channelId={channel.id} channelName={channel.name} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The channel-wide Amazon Business (B2B) pricing rule: mirror the retail
 * price by default, or a percent/fixed discount. Applies to every listing
 * that already carries a business price and has no per-listing override of
 * its own — see the "Business price rule" control on each listing's own
 * Product Data panel for that override, and lib/business-price.ts for the
 * precedence between the two.
 *
 * A price write never *creates* a business offer on a listing that doesn't
 * have one, whatever this rule says — this only decides what to set it to
 * when the listing already has one.
 */
function BusinessPricingRow({ channelId, channelName }: { channelId: string; channelName: string }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['business-pricing'], queryFn: api.businessPricing });
  const current = data?.rules.find((r) => r.channelId === channelId);

  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<'mirror' | 'percent_off' | 'amount_off'>(current?.rule.mode ?? 'mirror');
  const [percent, setPercent] = useState(current?.rule.mode === 'percent_off' ? String(current.rule.percent) : '10');
  const [amount, setAmount] = useState(
    current?.rule.mode === 'amount_off' ? (current.rule.amountMinor / 100).toFixed(2) : '0.00',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const rule =
      mode === 'mirror'
        ? ({ mode: 'mirror' } as const)
        : mode === 'percent_off'
          ? ({ mode: 'percent_off', percent: Number(percent) } as const)
          : ({ mode: 'amount_off', amountMinor: Math.round(Number(amount) * 100) } as const);

    setSaving(true);
    setError(null);
    try {
      await api.setChannelBusinessPricing(channelId, rule);
      await queryClient.invalidateQueries({ queryKey: ['business-pricing'] });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="small muted">
        {channelName} business pricing: {current ? current.label : 'mirrors the retail price'}{' '}
        <button className="link small" onClick={() => setEditing(true)}>
          change
        </button>
      </div>
    );
  }

  return (
    <div className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span className="muted">{channelName} business pricing:</span>
      <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)} disabled={saving}>
        <option value="mirror">Same as the retail price</option>
        <option value="percent_off">Percent below the retail price</option>
        <option value="amount_off">Amount below the retail price</option>
      </select>
      {mode === 'percent_off' && (
        <span>
          <input
            type="number"
            min={0}
            max={90}
            step="0.1"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            disabled={saving}
            style={{ width: 60 }}
          />
          %
        </span>
      )}
      {mode === 'amount_off' && (
        <span>
          $
          <input
            type="number"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={saving}
            style={{ width: 70 }}
          />
        </span>
      )}
      <button className="small" onClick={() => void save()} disabled={saving}>
        Save
      </button>
      <button className="link small" onClick={() => setEditing(false)} disabled={saving}>
        Cancel
      </button>
      {error && <span className="small error">{error}</span>}
      <div className="small muted" style={{ width: '100%' }}>
        Applies only to listings Amazon already reports a business price for — never creates one. A listing
        can still override this rule for itself from its Product Data panel. Quantity discount tiers are
        never touched.
      </div>
    </div>
  );
}

/**
 * Admin-only escape hatch for writes and queued changes stuck well past any
 * normal window — e.g. a batch of pushes interrupted by a server crash or
 * restart before recoverInterruptedBatch's boot-time sweep got a chance to
 * run, or a change staged and never pushed. Covers a queue row in any
 * status, not just one actively `executing` — a row can just as easily be
 * stuck at queued, conflict, needs_confirmation or failed. Unlike the
 * "Clear"/"Dismiss resolved" actions in the pending tray (PendingBar.tsx,
 * editor-accessible), this is willing to give up on a write still nominally
 * in flight or awaiting confirmation — so it only ever acts on rows past the
 * age threshold below, never on something still genuinely being watched.
 */
function StuckWrites() {
  const [minutes, setMinutes] = useState('60');
  const [preview, setPreview] = useState<{ writes: number; executing: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const parsedMinutes = Math.max(10, Math.round(Number(minutes) || 60));

  const previewMutation = useMutation({
    mutationFn: () => api.forceClearStaleWrites(parsedMinutes, true),
    onSuccess: (result) => {
      setError(null);
      if ('wouldClearWrites' in result) {
        setPreview({ writes: result.wouldClearWrites, executing: result.wouldClearExecuting });
      }
    },
    onError: (err: Error) => setError(err.message),
  });

  const clearMutation = useMutation({
    mutationFn: () => api.forceClearStaleWrites(parsedMinutes, false),
    onSuccess: (result) => {
      setError(null);
      setPreview(null);
      if ('clearedWrites' in result) {
        window.alert(
          `Cleared ${result.clearedWrites} write${result.clearedWrites === 1 ? '' : 's'} and ` +
            `${result.clearedExecuting} queued change${result.clearedExecuting === 1 ? '' : 's'}.`,
        );
      }
    },
    onError: (err: Error) => setError(err.message),
  });

  const forceClear = () => {
    if (!preview) return;
    const total = preview.writes + preview.executing;
    if (total === 0) return;
    if (
      window.confirm(
        `Force-clear ${preview.writes} stuck write${preview.writes === 1 ? '' : 's'} and ` +
          `${preview.executing} stuck queued change${preview.executing === 1 ? '' : 's'} untouched for over ` +
          `${parsedMinutes} minutes? This wipes local tracking only — it never touches the channel — and ` +
          `cannot be undone.`,
      )
    ) {
      clearMutation.mutate();
    }
  };

  const total = preview ? preview.writes + preview.executing : null;

  return (
    <div className="card">
      <p className="small muted">
        Force-settles writes and queued changes stuck for a long time — including ones still showing as
        "in flight" or "awaiting confirmation," which the regular Dismiss/Clear actions in the pending tray
        deliberately leave alone because they might still resolve on their own. This never re-checks the
        channel; it only stops tracking the write locally, so use it once you're confident the affected
        rows are genuinely abandoned (for example, after a server restart), not still legitimately being
        watched.
      </p>
      <div className="field" style={{ maxWidth: 260 }}>
        <label>Stuck for at least (minutes)</label>
        <input
          type="number"
          min={10}
          step="1"
          value={minutes}
          onChange={(e) => {
            setMinutes(e.target.value);
            setPreview(null);
          }}
        />
      </div>
      {error && <div className="alert error">{error}</div>}
      <div className="toolbar">
        <button disabled={previewMutation.isPending} onClick={() => previewMutation.mutate()}>
          {previewMutation.isPending ? 'Checking…' : 'Preview'}
        </button>
        {total !== null && total > 0 && (
          <button className="danger" disabled={clearMutation.isPending} onClick={forceClear}>
            {clearMutation.isPending ? 'Clearing…' : `Force clear ${total} stuck row${total === 1 ? '' : 's'}`}
          </button>
        )}
      </div>
      {total === 0 && <div className="small muted">Nothing is stuck past {parsedMinutes} minutes.</div>}
    </div>
  );
}

/**
 * The consent hand-off for a channel that authenticates by OAuth.
 *
 * It used to sit inside the credential form, which meant the only route to it
 * was a link labelled "replace credentials" — the one thing an admin whose
 * credentials already work would never think to click.
 *
 * Etsy and eBay differ in everything behind this button and in nothing in front
 * of it: start a consent flow, hand over a URL, come back and recheck.
 */
function OAuthConnect({
  channel,
  provider,
  authorize,
  onDone,
}: {
  channel: AdminChannel;
  provider: string;
  authorize: (channelId: string) => Promise<{ authorizeUrl: string }>;
  onDone: () => void;
}) {
  const [authorizeUrl, setAuthorizeUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: () => authorize(channel.id),
    onSuccess: ({ authorizeUrl: url }) => {
      setError(null);
      setAuthorizeUrl(url);
      // Popup blockers stop this often enough that the link below is the real
      // path; the new tab is only the convenient case.
      window.open(url, '_blank', 'noopener');
    },
    onError: (err: Error) => setError(err.message),
  });

  if (authorizeUrl) {
    return (
      <span className="small">
        <a href={authorizeUrl} target="_blank" rel="noreferrer">
          Open {provider} consent
        </a>{' '}
        <button className="link small" onClick={onDone}>
          recheck
        </button>
      </span>
    );
  }

  return (
    <>
      <button className="small" disabled={connect.isPending} onClick={() => connect.mutate()}>
        {connect.isPending
          ? 'Starting…'
          : `${channel.tokenExpiresAt ? 'Reconnect' : 'Connect'} ${provider}`}
      </button>
      {error && <div className="small error">{error}</div>}
    </>
  );
}

/**
 * Credential entry for one channel.
 *
 * Only non-empty fields are sent, so a partly filled form tops up what is
 * stored rather than clearing the rest.
 */
function CredentialForm({
  channel,
  onSaved,
}: {
  channel: AdminChannel;
  onSaved: () => void;
}) {
  const fields = credentialFields[channel.type] ?? [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const filled = (name: string) => (values[name] ?? '').trim();
  const missingRequired = fields.some((f) => f.required && !filled(f.name));

  const save = useMutation({
    mutationFn: () => {
      const payload = Object.fromEntries(
        fields.map((f) => [f.name, filled(f.name)]).filter(([, v]) => v !== ''),
      ) as Record<string, string>;
      return api.setChannelCredentials(channel.id, payload);
    },
    onSuccess: () => {
      setValues({});
      setError(null);
      onSaved();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="card">
      {error && <div className="alert error">{error}</div>}

      {fields.map((field) => (
        <div className="field" key={field.name}>
          <label>
            {field.label}
            {field.required && ' *'}
          </label>
          <input
            type={field.secret ? 'password' : 'text'}
            autoComplete="off"
            value={values[field.name] ?? ''}
            onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.value }))}
          />
          {field.hint && <div className="small muted">{field.hint}</div>}
        </div>
      ))}

      <div className="toolbar">
        <button
          className="primary"
          disabled={missingRequired || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      {channel.hasCredentials && (
        <div className="small muted">
          This channel already has credentials stored. They cannot be read back — saving replaces
          the fields you fill in and leaves the rest untouched.
        </div>
      )}
    </div>
  );
}

function Users({ currentUser }: { currentUser: CurrentUser }) {
  const queryClient = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('viewer');
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['users'], queryFn: api.users });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  const create = useMutation({
    mutationFn: () => api.createUser(email, name, role),
    onSuccess: (result) => {
      // Shown once and never again — there is no mail transport, so the admin
      // passes it along directly.
      setTempPassword(result.tempPassword);
      setEmail('');
      setName('');
      setShowNew(false);
      setError(null);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) => api.updateUser(id, patch),
    onSuccess: refresh,
    onError: (err: Error) => setError(err.message),
  });

  const reset = useMutation({
    mutationFn: (id: string) => api.resetUserPassword(id),
    onSuccess: (result) => {
      setTempPassword(result.tempPassword);
      refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  if (isLoading) return <div className="empty-state">Loading…</div>;

  return (
    <div>
      <div className="toolbar">
        <button className="primary" onClick={() => setShowNew((v) => !v)}>
          {showNew ? 'Cancel' : 'Add user'}
        </button>
      </div>

      {error && <div className="alert error">{error}</div>}

      {tempPassword && (
        <div className="alert warn">
          One-time password: <strong className="sku">{tempPassword}</strong> — copy it now, it is
          not shown again. The user must change it at first login.{' '}
          <button className="link" onClick={() => setTempPassword(null)}>dismiss</button>
        </div>
      )}

      {showNew && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="field">
            <label>Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              <option value="viewer">Viewer — read only</option>
              <option value="editor">Editor — can push prices and titles</option>
              <option value="admin">Admin — plus users and credentials</option>
            </select>
          </div>
          <button className="primary" disabled={!email || create.isPending} onClick={() => create.mutate()}>
            Create
          </button>
        </div>
      )}

      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last login</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((row: any) => (
              <tr key={row.id}>
                <td>{row.email}</td>
                <td>{row.name ?? <span className="muted">—</span>}</td>
                <td>
                  <select
                    value={row.role}
                    disabled={row.id === currentUser.id}
                    onChange={(e) => update.mutate({ id: row.id, patch: { role: e.target.value } })}
                  >
                    <option value="viewer">viewer</option>
                    <option value="editor">editor</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td>
                  {row.active ? (
                    <span className="badge ok">active</span>
                  ) : (
                    <span className="badge">deactivated</span>
                  )}
                  {row.mustChangePassword && <> <span className="badge warn">must reset</span></>}
                </td>
                <td className="small muted">
                  {row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleString() : 'never'}
                </td>
                <td>
                  <button className="link small" onClick={() => reset.mutate(row.id)}>
                    reset password
                  </button>
                  {row.id !== currentUser.id && (
                    <button
                      className="link small"
                      onClick={() =>
                        update.mutate({ id: row.id, patch: { active: !row.active } })
                      }
                    >
                      {row.active ? 'deactivate' : 'reactivate'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
